import {
  type WorkflowDefinitionDocument,
  type WorkflowStatusRule,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  statusConfigurationError,
  statusScopeNode,
  statusStepOutcomeChoices,
} from './workflow-status-authoring';

const outcomeEvent = 'step_outcome_followed';

function exactRules(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  outcomeCode: string,
): WorkflowStatusRule[] {
  return (
    document.statusConfiguration?.rules.filter(
      (rule) =>
        rule.eventCode === outcomeEvent &&
        rule.sourceNodeId === nodeId &&
        rule.outcomeCode === outcomeCode,
    ) ?? []
  );
}

export function stepOutcomeStatusContextError(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): string | null {
  if (!statusScopeNode(document, nodeId))
    return 'This work step is missing or ambiguous. Reload its status settings.';
  return document.statusConfiguration
    ? statusConfigurationError(document, null, document.statusConfiguration)
    : null;
}

/** Read the same workflow rule used by the general status editor; never invent a second mapping. */
export function readStepOutcomeStatus(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  outcomeCode: string,
): { statusCode: string; ruleId: string | null; error: string | null } {
  const rules = exactRules(document, nodeId, outcomeCode);
  if (rules.length > 1)
    return {
      statusCode: '',
      ruleId: null,
      error:
        'More than one workflow status rule uses this step outcome. Resolve the duplicate in Workflow statuses before editing its path.',
    };
  return {
    statusCode: rules[0]?.statusCode ?? '',
    ruleId: rules[0]?.id ?? null,
    error: stepOutcomeStatusContextError(document, nodeId),
  };
}

export function stepOutcomeStatusNotice(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): string | null {
  const rules = document.statusConfiguration?.rules ?? [];
  if (rules.some((rule) => rule.eventCode === 'workflow_completed'))
    return 'Other automatic status rules remain configured. An outcome status takes priority over step changes; a Process completes rule can then set the final status when the process ends.';
  if (
    rules.some(
      (rule) =>
        ['step_entered', 'step_returned'].includes(rule.eventCode) ||
        (rule.sourceNodeId === nodeId && rule.eventCode === 'step_completed'),
    )
  )
    return 'Other automatic status rules remain configured. A status selected for an outcome takes priority over step changes when that path is followed.';
  return null;
}

/** Apply only exact outcome rules after route validation, in the caller's single document transaction. */
export function applyStepOutcomeStatuses(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  mapping: Readonly<Record<string, string>>,
  outcomeCodes: readonly string[],
): WorkflowDefinitionDocument {
  const error = stepOutcomeStatusContextError(document, nodeId);
  if (error) throw new Error(error);
  const choices = statusStepOutcomeChoices(document, nodeId);
  if (
    !outcomeCodes.length ||
    new Set(outcomeCodes).size !== outcomeCodes.length ||
    choices.length !== outcomeCodes.length ||
    choices.some((choice) => !outcomeCodes.includes(choice.code)) ||
    Object.keys(mapping).length !== outcomeCodes.length ||
    Object.keys(mapping).some((code) => !outcomeCodes.includes(code))
  )
    throw new Error(
      'The step outcomes changed. Review the latest paths before applying workflow statuses.',
    );
  const config = document.statusConfiguration;
  if (!config) {
    if (Object.values(mapping).some(Boolean))
      throw new Error(
        'Create workflow statuses in Workflow overview before choosing a status for an outcome.',
      );
    return document;
  }
  if (
    Object.values(mapping).some(
      (code) =>
        typeof code !== 'string' ||
        (code !== '' &&
          !config.statuses.some((status) => status.code === code)),
    )
  )
    throw new Error(
      'A selected workflow status is no longer available. Choose a current workflow status before applying paths.',
    );
  let changed = false;
  const rules = config.rules.flatMap((rule) => {
    if (
      rule.eventCode !== outcomeEvent ||
      rule.sourceNodeId !== nodeId ||
      !outcomeCodes.includes(rule.outcomeCode ?? '')
    )
      return [rule];
    const statusCode = mapping[rule.outcomeCode!];
    if (statusCode === rule.statusCode) return [rule];
    changed = true;
    return statusCode ? [{ ...rule, statusCode }] : [];
  });
  for (const outcomeCode of outcomeCodes) {
    if (
      !mapping[outcomeCode] ||
      exactRules(document, nodeId, outcomeCode).length
    )
      continue;
    changed = true;
    rules.push({
      id: `rule_${crypto.randomUUID().replaceAll('-', '')}`,
      eventCode: outcomeEvent,
      sourceNodeId: nodeId,
      requirementId: null,
      outcomeCode,
      statusCode: mapping[outcomeCode],
    });
  }
  if (!changed) return document;
  const next = { ...config, rules };
  const nextError = statusConfigurationError(document, null, next);
  if (nextError) throw new Error(nextError);
  return {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.11',
    ),
    statusConfiguration: next,
  };
}
